// 装備（ハクスラ式）：武器（キャラ専用）・防具・アクセサリー（内部ID netsuke、共通）。段（章）で基礎値、追加効果（銘）はレアリティで0〜4個。
// 強化（銭・段階で費用増）、合成（同じ装備でレア度が同じか上のもの＋銭。回数は鍛冶屋のLvまで）、
// ロック、売却（銭のみ。一括売却・自動売却）。
import { DEX_ENEMIES, isDexEnemy } from '../data/enemies';
import { isPlayerAvatar, isPremiumAvatar } from '../data/player-avatars';
import { hasPremiumAvatars } from './monthly';
import { EQUIP_LINES, LINE_BY_ID, LOW_TIER_PREFIX, MEIBUTSU, YOUKAI_ARMOR_TRAITS, YOUKAI_NECK_TRAITS, type EquipLine } from '../data/equip-lines';
import { random, randInt, weighted, type RngHolder } from '../rng';
import type { CharId } from '../types';
import { CHAR_IDS } from '../types';
import { CONFIG } from './config';
import { DISCRETE_KEYS, MOD_LABEL, type Effect, type ModKey } from './mods';
import {
  RARITIES,
  type Affix,
  type AffixId,
  type AutoSell,
  type EquipItem,
  type EquipSlot,
  type MeiRoll,
  type OpResult,
  type Profile,
  type Rarity,
  type Resources,
} from './profile';

export interface EquipDef {
  id: string;
  name: string;
  slot: EquipSlot;
  charId: CharId | null;
  tier: number;
  /** 敵固有のドロップ（図鑑・周回先選び用） */
  enemyId?: string;
  /** 系統（data/equip-lines の id）。名物・妖の装備は無し */
  line?: string;
  /** 名物（極でだけ出る一点もの） */
  meibutsu?: boolean;
  /** 固有効果（種類で決まる。レアリティ・強化によらない） */
  traits: Effect[];
  /** 戦い方が変わる固有（戦闘のフラグ。系統で決まる） */
  flags?: string[];
  /** 相性の術。持っていくと固有効果（traits）が synergyMult 倍 */
  jutsu?: string;
}

/** 固有効果の値（段 t、名物は k 倍）。%は小数（0.05=5%）、速さ・初期陣材は整数 */
export function traitValue(key: ModKey, tier: number, k = 1): number {
  const t = tier;
  // 系統を選ぶ理由になるよう、固有効果は銘より大きく（traitMult。速さ・初期陣材は別の式）
  k *= key === 'agiFlat' || key === 'matStart' ? 1 : CONFIG.equipment.traitMult;
  const pct = (v: number) => Math.round(v * k * 1000) / 1000;
  switch (key) {
    case 'agiFlat':
      // 1+floor(t/3)（+1〜3）→ 3+1.2t（+4〜11）。CTB の崖がなくなり速さ1つずつが効くので、攻撃の固有（段6で +8.5%）の 7割ほどの戦力に（BALANCE.md 38章）
      return Math.max(1, Math.round((3 + 1.2 * t) * k));
    case 'matStart':
      return Math.max(1, Math.round((1 + Math.floor(t / 4)) * k));
    case 'core':
    case 'boss':
    case 'neutral':
      return pct(0.015 + 0.005 * t);
    case 'splash':
      return pct(0.02 + 0.006 * t);
    case 'lifesteal':
      return pct(0.004 + 0.0016 * t);
    default:
      return pct(0.01 + 0.004 * t);
  }
}

/** 固有効果の表示（例「術の威力+3.4%」「速さ+2」） */
export function traitText(e: Effect): string {
  const flat = e.key === 'agiFlat' || e.key === 'matStart' || DISCRETE_KEYS.has(e.key);
  return `${MOD_LABEL[e.key]}+${flat ? e.value : `${Math.round(e.value * 1000) / 10}%`}`;
}

/** 系統と段から装備ID。既存の系統は昔のID（w_キャラ_段・a_段・n_段）のまま */
export function lineDefId(line: EquipLine, tier: number): string {
  const short = line.id.slice(2);
  if (line.slot === 'weapon') return line.legacy ? `w_${line.charId}_${tier}` : `w_${line.charId}_${short}_${tier}`;
  const pre = line.slot === 'armor' ? 'a' : 'n';
  return line.legacy ? `${pre}_${tier}` : `${pre}_${short}_${tier}`;
}

function buildDefs(): Record<string, EquipDef> {
  const out: Record<string, EquipDef> = {};
  for (const line of EQUIP_LINES) {
    for (let t = 1; t <= 10; t++) {
      const id = lineDefId(line, t);
      const name = t <= 5 ? `${LOW_TIER_PREFIX[t - 1]}の${line.name}` : line.named[t - 6];
      out[id] = {
        id,
        name,
        slot: line.slot,
        charId: line.charId,
        tier: t,
        line: line.id,
        traits: [{ key: line.trait, value: traitValue(line.trait, t) }],
        ...(line.flag ? { flags: [line.flag] } : {}),
        ...(line.jutsu ? { jutsu: line.jutsu } : {}),
      };
    }
  }
  for (const m of MEIBUTSU) {
    // 名物は1つ目の固有効果が同じ系統の戦い方（フラグ）と相性の術を引き継ぐ
    const like = EQUIP_LINES.find((l) => l.slot === m.slot && l.charId === m.charId && l.trait === m.traits[0].key);
    out[m.id] = {
      id: m.id,
      name: m.name,
      slot: m.slot,
      charId: m.charId,
      tier: m.tier,
      meibutsu: true,
      traits: m.traits.map((x) => ({ key: x.key, value: traitValue(x.key, m.tier, x.k) })),
      ...(like?.flag ? { flags: [like.flag] } : {}),
      ...(like?.jutsu ? { jutsu: like.jutsu } : {}),
    };
  }
  for (const e of DEX_ENEMIES) {
    const armor = e.no % 2 === 1;
    const id = `u_${e.id}`;
    const list = armor ? YOUKAI_ARMOR_TRAITS : YOUKAI_NECK_TRAITS;
    const key = list[Math.floor(e.no / 2) % list.length];
    out[id] = { id, name: `${e.name}の${armor ? '皮衣' : '首飾り'}`, slot: armor ? 'armor' : 'netsuke', charId: null, tier: e.chapter, enemyId: e.id, traits: [{ key, value: traitValue(key, e.chapter) }] };
  }
  return out;
}
export const EQUIPMENT: Record<string, EquipDef> = buildDefs();
/** 部位（武器はキャラ）ごとの系統 */
export const linesFor = (slot: EquipSlot, charId: CharId | null = null) => EQUIP_LINES.filter((l) => l.slot === slot && (slot !== 'weapon' || l.charId === charId));

export type AffixUnit = '%' | 'flat';
export interface AffixDef {
  name: string;
  /** 表示の単位。'%' は value 0.05 = 5% */
  unit: AffixUnit;
  /** 1段目の値の幅の基準（段で (meiTierScaleBase + meiTierScalePer*段) 倍）。fixed は段・品質によらず一定 */
  lo: number;
  hi: number;
  /** loadout に足す効果のキーと、value に掛ける符号（復活は -1 手番） */
  effect: Effect['key'];
  sign?: 1 | -1;
  /** 段・品質によらない固定値（陣材収入+1、復活1手番早い）。1人の装備全体で1つ分まで効く（loadout.collectMods） */
  fixed?: boolean;
  /** このレアリティ以上でだけ出る（ルールを変える効果は極だけ） */
  minRarity?: Rarity;
  /** ハード以上（difficulty>=1）のドロップでだけ出る */
  hard?: boolean;
  /** デメリット付き（1つの装備に1つまで）。extra は段・品質によらない固定のデメリット */
  drawback?: boolean;
  extra?: Effect[];
  /** 戦闘のフラグ（反撃・再生など）。効果の値は使わない */
  flags?: string[];
  /** 表示：{v} に値 */
  text: string;
}

/** 追加効果（銘）の候補。battle の BattleLoadout に既にある項目だけ */
export const AFFIX_POOL: Record<AffixId, AffixDef> = {
  atk: { name: '剛の銘', unit: '%', lo: 0.03, hi: 0.08, effect: 'atk', text: '攻撃+{v}' },
  hp: { name: '寿の銘', unit: '%', lo: 0.04, hi: 0.1, effect: 'hp', text: 'HP+{v}' },
  def: { name: '堅の銘', unit: '%', lo: 0.04, hi: 0.1, effect: 'def', text: '防御+{v}' },
  // 速さ+1 は戦力で攻撃+0.6% ぶん。剛の銘（攻撃+3〜8%）と釣り合うように 2〜6 → 5〜12（段の倍率 ×0.4 の後で +1〜2 → +2〜5。BALANCE.md 38章）
  agi: { name: '疾の銘', unit: 'flat', lo: 5, hi: 12, effect: 'agiFlat', text: '速さ+{v}' },
  core: { name: '城崩しの銘', unit: '%', lo: 0.04, hi: 0.1, effect: 'core', text: '本陣への与ダメ+{v}' },
  boss: { name: '鬼殺しの銘', unit: '%', lo: 0.05, hi: 0.12, effect: 'boss', text: 'ボスへの与ダメ+{v}' },
  neutral: { name: '妖祓いの銘', unit: '%', lo: 0.05, hi: 0.12, effect: 'neutral', text: '中立への与ダメ+{v}' },
  skillPower: { name: '術の銘', unit: '%', lo: 0.04, hi: 0.1, effect: 'skillPower', text: '術の威力+{v}' },
  buildHp: { name: '礎の銘', unit: '%', lo: 0.06, hi: 0.15, effect: 'buildHp', text: '建物HP+{v}' },
  buildPower: { name: '匠の銘', unit: '%', lo: 0.04, hi: 0.1, effect: 'buildPower', text: '建物の効果+{v}' },
  lifesteal: { name: '吸精の銘', unit: '%', lo: 0.01, hi: 0.03, effect: 'lifesteal', text: '与ダメの{v}を吸収' },
  matStart: { name: '蓄えの銘', unit: 'flat', lo: 1, hi: 3, effect: 'matStart', text: '初期陣材+{v}' },
  potionHeal: { name: '癒の銘', unit: '%', lo: 0.03, hi: 0.08, effect: 'potionHeal', text: '回復薬の回復+{v}（最大HP比）' },
  matIncome: { name: '商の銘', unit: 'flat', lo: 1, hi: 1, effect: 'matIncome', fixed: true, minRarity: 'ssr', text: '毎手番の陣材+{v}' },
  respawn: { name: '再起の銘', unit: 'flat', lo: 1, hi: 1, effect: 'respawn', sign: -1, fixed: true, minRarity: 'ssr', text: '復活が{v}手番早い' },
  territory: { name: '陣の銘', unit: '%', lo: 0.03, hi: 0.08, effect: 'territory', text: '陣地率+{v}' },
  guard: { name: '守の銘', unit: '%', lo: 0.04, hi: 0.1, effect: 'taken', sign: -1, text: '被ダメ-{v}' },
  splash: { name: '散の銘', unit: '%', lo: 0.06, hi: 0.15, effect: 'splash', text: '術の範囲ダメージ+{v}' },
  counter: { name: '返の銘', unit: 'flat', lo: 0, hi: 0, effect: 'atk', fixed: true, minRarity: 'sr', flags: ['counter'], text: '隣から攻撃されると反撃' },
  regen: { name: '再生の銘', unit: 'flat', lo: 0, hi: 0, effect: 'atk', fixed: true, minRarity: 'ssr', flags: ['regen'], text: '手番の最初にHPを回復' },
  luck: { name: '吉の銘', unit: 'flat', lo: 1, hi: 1, effect: 'diceMin', fixed: true, minRarity: 'ssr', hard: true, text: '移動の最低歩数+{v}' },
  // デメリット付きの強い銘（ハード以上・優以上。1つの装備に1つまで）
  sutemi: { name: '捨て身の銘', unit: '%', lo: 0.6, hi: 1.2, effect: 'atk', minRarity: 'sr', hard: true, drawback: true, extra: [{ key: 'taken', value: 0.2 }], text: '攻撃+{v}（被ダメ+20%）' },
  donju: { name: '鈍重の銘', unit: '%', lo: 0.9, hi: 1.8, effect: 'hp', minRarity: 'sr', hard: true, drawback: true, extra: [{ key: 'move', value: -1 }], text: 'HP+{v}（移動-1）' },
  jutou: { name: '呪刀の銘', unit: '%', lo: 0.8, hi: 1.6, effect: 'skillPower', minRarity: 'sr', hard: true, drawback: true, extra: [{ key: 'potionHeal', value: -0.5 }], text: '術の威力+{v}（回復薬の回復-50%）' },
  kyouhon: { name: '狂奔の銘', unit: 'flat', lo: 1, hi: 1, effect: 'move', fixed: true, minRarity: 'sr', hard: true, drawback: true, extra: [{ key: 'def', value: -0.3 }], text: '移動+{v}（防御-30%）' },
  kesshi: { name: '決死の銘', unit: '%', lo: 0.9, hi: 1.8, effect: 'core', minRarity: 'sr', hard: true, drawback: true, extra: [{ key: 'hp', value: -0.15 }], text: '本陣への与ダメ+{v}（HP-15%）' },
  // 賽3つ（rules 2）：賽の合計・役で効く
  chou: { name: '丁の銘', unit: '%', lo: 0.06, hi: 0.15, effect: 'diceEven', text: '賽の合計が偶数の手番、与ダメ+{v}' },
  han: { name: '半の銘', unit: '%', lo: 0.06, hi: 0.15, effect: 'diceOdd', text: '賽の合計が奇数の手番から、次の手番まで被ダメ-{v}' },
  oome: { name: '大目の銘', unit: '%', lo: 0.05, hi: 0.12, effect: 'diceHigh', text: '賽の合計が10以上の手番、与ダメ+{v}' },
  kome: { name: '小目の銘', unit: 'flat', lo: 1, hi: 1, effect: 'diceLow', fixed: true, minRarity: 'sr', text: '賽の合計が9以下の手番、移動+{v}' },
  soroi: { name: '揃の銘', unit: '%', lo: 0.06, hi: 0.14, effect: 'dicePair', text: '賽に同じ目がある手番、与ダメ+{v}' },
  yakusha: { name: '役者の銘', unit: '%', lo: 0.2, hi: 0.4, effect: 'diceRole', text: '役が出た手番、与ダメ+{v}' },
  saijutsu: { name: '賽術の銘', unit: '%', lo: 0.2, hi: 0.5, effect: 'diceSkill', text: '術の賽の上がり幅+{v}' },
};

/** 業物（セット）：同じシリーズを2部位・3部位そろえると効果（ハード以上のドロップで出る） */
export interface EquipSetDef {
  id: string;
  name: string;
  two: Effect[];
  three: Effect[];
  /** 3部位の戦闘のフラグ */
  threeFlags?: string[];
  text2: string;
  text3: string;
}
export const EQUIP_SETS: readonly EquipSetDef[] = [
  { id: 'idaten', name: '韋駄天', two: [{ key: 'agi', value: 0.08 }], three: [{ key: 'move', value: 1 }], text2: '速さ+8%', text3: '移動+1' },
  { id: 'yata', name: '八咫', two: [{ key: 'skillPower', value: 0.1 }], three: [{ key: 'range', value: 1 }], text2: '術の威力+10%', text3: '射程+1' },
  { id: 'kikoku', name: '鬼哭', two: [{ key: 'core', value: 0.15 }], three: [{ key: 'cd', value: -1 }], text2: '本陣への与ダメ+15%', text3: '術の待ち-1' },
  { id: 'fudo', name: '不動', two: [{ key: 'taken', value: -0.1 }], three: [], threeFlags: ['regen'], text2: '被ダメ-10%', text3: '手番の最初にHPを回復' },
  { id: 'shura', name: '修羅', two: [{ key: 'atk', value: 0.08 }], three: [{ key: 'lifesteal', value: 0.05 }], text2: '攻撃+8%', text3: '与ダメの5%を吸収' },
  { id: 'kunitori', name: '国盗', two: [{ key: 'territory', value: 0.02 }], three: [{ key: 'matIncome', value: 1 }], text2: '陣地率+2%', text3: '毎手番の陣材+1' },
];
export const EQUIP_SET_BY_ID: Readonly<Record<string, EquipSetDef>> = Object.fromEntries(EQUIP_SETS.map((s) => [s.id, s]));
/** 装備中のシリーズごとの部位数と、発動している効果（2部位・3部位） */
export function setBonuses(items: readonly Pick<EquipItem, 'set'>[]): { def: EquipSetDef; count: number; effects: Effect[]; flags: string[] }[] {
  const n = new Map<string, number>();
  for (const it of items) if (it.set && EQUIP_SET_BY_ID[it.set]) n.set(it.set, (n.get(it.set) ?? 0) + 1);
  return [...n].map(([id, count]) => {
    const def = EQUIP_SET_BY_ID[id];
    return { def, count, effects: [...(count >= 2 ? def.two : []), ...(count >= 3 ? def.three : [])], flags: count >= 3 ? (def.threeFlags ?? []) : [] };
  });
}
export const AFFIX_IDS = Object.keys(AFFIX_POOL) as AffixId[];

const affixScale = (tier: number) => {
  const c = CONFIG.equipment;
  return (c.meiTierScaleBase + c.meiTierScalePer * tier) * c.affixValueMult;
};

/** その段での値の幅 */
export function affixRange(id: AffixId, tier: number): { lo: number; hi: number } {
  const d = AFFIX_POOL[id];
  if (d.fixed) return { lo: d.lo, hi: d.hi };
  const k = affixScale(tier);
  return { lo: affixRound(id, d.lo * k), hi: affixRound(id, d.hi * k) };
}

function affixRound(id: AffixId, raw: number) {
  return AFFIX_POOL[id].unit === 'flat' ? Math.max(1, Math.round(raw)) : Math.round(raw * 1000) / 1000;
}

/** 品質 q（0..1）での値 */
export function affixValue(id: AffixId, tier: number, q: number): number {
  const d = AFFIX_POOL[id];
  if (d.fixed) return d.lo;
  return affixRound(id, (d.lo + (d.hi - d.lo) * q) * affixScale(tier));
}

const rarityOk = (id: AffixId, rarity: Rarity) => {
  const min = AFFIX_POOL[id].minRarity;
  return !min || RARITIES.indexOf(rarity) >= RARITIES.indexOf(min);
};

/** 1つ抽選（exclude の効果は除く＝重複なし。rarity 未満でしか出ない効果は除く。hard でなければハード以上の銘は除く。デメリット付きは1つまで） */
export function rollAffix(h: RngHolder, tier: number, exclude: readonly AffixId[] = [], rarity: Rarity = 'ssr', hard = false): Affix {
  const hasDrawback = exclude.some((x) => AFFIX_POOL[x]?.drawback);
  const ids = AFFIX_IDS.filter((x) => !exclude.includes(x) && rarityOk(x, rarity) && (hard || !AFFIX_POOL[x].hard) && !(hasDrawback && AFFIX_POOL[x].drawback));
  const id = weighted<AffixId>(h, ids.map((x) => ({ item: x, weight: (CONFIG.equipment.affixWeights[x] ?? 1) * (AFFIX_POOL[x].drawback ? CONFIG.equipment.drawbackWeight : 1) })));
  const d = AFFIX_POOL[id];
  const q = d.fixed ? 1 : Math.round(random(h) * 100) / 100;
  return { id, value: affixValue(id, tier, q), q };
}

/** レアリティに応じた個数を重複なしで抽選 */
export function rollAffixes(h: RngHolder, tier: number, rarity: Rarity, opts: { hard?: boolean; shin?: boolean } = {}): Affix[] {
  const [lo, hi] = opts.shin ? CONFIG.equipment.shinAffixCount : CONFIG.equipment.affixCount[rarity];
  const n = lo + randInt(h, hi - lo + 1);
  const out: Affix[] = [];
  for (let i = 0; i < n; i++) out.push(rollAffix(h, tier, out.map((a) => a.id), rarity, !!opts.hard));
  return out;
}

const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;
/** 表示用の文（例「剛の銘：攻撃+5.2%」） */
export function affixText(a: Pick<Affix, 'id' | 'value'>): string {
  const d = AFFIX_POOL[a.id];
  if (!d) return '';
  return `${d.name}：${d.text.replace('{v}', d.unit === '%' ? pct(a.value) : String(a.value))}`;
}

/** 旧保存データの銘1つ → affixes。値はそのまま、q は旧来の幅（affixValueMult を掛ける前）から逆算 */
export function meiToAffix(m: MeiRoll, tier: number): Affix {
  const d = AFFIX_POOL[m.id];
  if (!d) return { id: 'atk', value: m.value, q: 0.5 };
  if (d.fixed) return { id: m.id, value: d.lo, q: 1 };
  const k = CONFIG.equipment.meiTierScaleBase + CONFIG.equipment.meiTierScalePer * tier;
  const q = d.hi > d.lo ? (m.value / k - d.lo) / (d.hi - d.lo) : 1;
  return { id: m.id, value: m.value, q: Math.round(Math.max(0, Math.min(1, q)) * 100) / 100 };
}

export const levelCap = (item: Pick<EquipItem, 'lb'>) => CONFIG.equipment.levelCapBase + CONFIG.equipment.levelCapPerLb * item.lb;

/** 装備の固定値（HP・攻撃・防御・速さ） */
export function itemStats(item: EquipItem): { hp: number; atk: number; def: number; agi: number } {
  const c = CONFIG.equipment;
  const k = c.rarityMult[item.rarity] * (1 + c.enhancePerLevel * item.level);
  const t = item.tier;
  const def = EQUIPMENT[item.defId];
  const st = (def?.line ? LINE_BY_ID[def.line]?.stat : undefined) ?? {};
  const m = (key: 'atk' | 'hp' | 'def' | 'agi', dflt: number) => st[key] ?? dflt;
  // 部位の基礎値（武器＝攻撃、防具＝HP・防御、根付＝HP・速さ）。系統の stat で配分を変え、部位に無い値は他の部位の基礎値の割合で足す
  const wAtk = c.weapon.atkBase + c.weapon.atkPerTier * t;
  const aHp = c.armor.hpBase + c.armor.hpPerTier * t;
  const aDef = c.armor.defBase + c.armor.defPerTier * t;
  const nHp = c.netsuke.hpBase + c.netsuke.hpPerTier * t;
  const nAgi = c.netsuke.agiBase + c.netsuke.agiPerTier * t;
  if (item.slot === 'weapon') return { hp: aHp * m('hp', 0) * k, atk: wAtk * m('atk', 1) * k, def: aDef * m('def', 0) * k, agi: nAgi * m('agi', 0) * k };
  if (item.slot === 'armor') return { hp: aHp * m('hp', 1) * k, atk: wAtk * m('atk', 0) * k, def: aDef * m('def', 1) * k, agi: nAgi * m('agi', 0) * k };
  return { hp: nHp * m('hp', 1) * k, atk: wAtk * m('atk', 0) * k, def: aDef * m('def', 0) * k, agi: nAgi * m('agi', 1) * k };
}

/** 装備の固有効果（種類で決まる） */
export const itemTraitEffects = (item: Pick<EquipItem, 'defId'>): Effect[] => EQUIPMENT[item.defId]?.traits ?? [];
/** 戦い方が変わる固有の表示（battle/config の flags の値と合わせる） */
export const EQUIP_FLAG_TEXT: Record<string, string> = {
  eq_ignite: '術を当てた敵を燃やす',
  eq_paint: '通常攻撃で敵のマスと周りを自陣に',
  eq_knock: '通常攻撃で1マス押し出し、遅らせる',
  eq_volley: '攻撃した敵を、射程に入れた自分の櫓・大筒なども一緒に撃つ',
  eq_bind: '通常攻撃で2手番の足止め',
  eq_stagger: '通常攻撃で敵の行動を遅らせる',
  eq_cleave: '通常攻撃で周りの敵にも50%',
  eq_double: '通常攻撃のあと50%で追撃',
  counter: '隣から攻撃されると反撃',
  kaeshi: '隣から攻撃されると斬り返す',
  conqueror: '自陣の中で与ダメ+20%',
  momentum: '2マス以上動くと与ダメ+20%',
  execution: 'HP35%以下の敵へ与ダメ+25%',
  siege: '本陣へ与ダメ+25%',
  fushin_repair: '手番の最初に近くの自分の建物を直す',
  duelist: '敵ヒーローへ与ダメ+20%',
  riposte: '待機の次の攻撃は与ダメ+35%',
  regen: '手番の最初にHP5%回復',
  sanctuary: '自陣の中で防御+25%・HP回復',
};
/** 装備の戦い方が変わる固有（戦闘のフラグ） */
export const itemTraitFlags = (item: Pick<EquipItem, 'defId'>): string[] => EQUIPMENT[item.defId]?.flags ?? [];

/** 装備の名前（業物はシリーズ名を前に、神品は後ろに） */
export function equipName(item: Pick<EquipItem, 'defId' | 'set' | 'shin'>): string {
  const base = EQUIPMENT[item.defId]?.name ?? item.defId;
  const set = item.set ? EQUIP_SET_BY_ID[item.set] : undefined;
  return `${set ? `${set.name}・` : ''}${base}${item.shin ? '【神品】' : ''}`;
}

export const inventoryCap = (p: Profile) => Math.min(CONFIG.equipment.inventoryMax, CONFIG.equipment.inventoryBase + CONFIG.equipment.inventoryPerSlotPack * p.slotPacks);
/** 所持枠+50 をもう買えないか（上限 inventoryMax に届いた） */
export const inventoryFull = (p: Profile) => inventoryCap(p) >= CONFIG.equipment.inventoryMax;

/** ドロップや購入で得る装備の中身（個体IDはまだ無い） */
export interface EquipDrop {
  defId: string;
  rarity: Rarity;
  affixes: Affix[];
  /** 業物のシリーズ・神品（ハード以上） */
  set?: string;
  shin?: boolean;
}

export interface AddItemResult {
  item: EquipItem | null;
  zeni: number;
  /** 'auto'＝自動売却、'overflow'＝所持枠いっぱいで売却 */
  sold?: 'auto' | 'overflow';
}

const rarityRank = (r: Rarity) => RARITIES.indexOf(r);
/** 自動売却の対象か（拾った時点で autoSell 以下のレアリティ、かつ段が maxTier 以下。maxTier が無ければ段は問わない） */
export function autoSellTarget(setting: AutoSell | undefined, rarity: Rarity, tier = 1, maxTier?: number): boolean {
  if (!setting || setting === 'none') return false;
  if (maxTier !== undefined && tier > maxTier) return false;
  return rarityRank(rarity) <= rarityRank(setting);
}

/**
 * 装備を所持品に加える。pickup（戦闘のドロップ）なら自動売却の設定に従う。
 * 所持枠がいっぱいなら売却して銭にする（消失させない）
 */
export function addItem(p: Profile, drop: EquipDrop, opts: { pickup?: boolean } = {}): AddItemResult {
  const def = EQUIPMENT[drop.defId];
  const item: EquipItem = {
    id: p.nextId++,
    defId: def.id,
    slot: def.slot,
    charId: def.charId,
    tier: def.tier,
    rarity: drop.rarity,
    level: 0,
    lb: 0,
    affixes: (drop.affixes ?? []).map((a) => ({ ...a })),
    rerolls: 0,
    rerollDay: '',
    ...(drop.set && EQUIP_SET_BY_ID[drop.set] ? { set: drop.set } : {}),
    ...(drop.shin ? { shin: true } : {}),
  };
  if (opts.pickup && autoSellTarget(p.settings?.autoSell, item.rarity, item.tier, p.settings?.autoSellTier)) return { item: null, zeni: sellValue(item), sold: 'auto' };
  if (p.items.length >= inventoryCap(p)) return { item: null, zeni: sellValue(item), sold: 'overflow' };
  p.items.push(item);
  return { item, zeni: 0 };
}

/** ドロップ抽選。武器は出撃キャラ向けが50%、残りは他の3人。防具・根付は敵固有品か段の装備（系統は等確率）。
 *  極は一定の確率で、その段までに出る同じ部位の名物になる（武器はキャラも合わせる） */
export function rollEquipDrop(
  h: RngHolder,
  tier: number,
  sortieChar: CharId,
  enemyIds: string[] = [],
  rarityWeights: Partial<Record<Rarity, number>> = CONFIG.rewards.rarityWeights,
  /** 出撃していないキャラの武器（benchWeaponChance の追加ドロップ） */
  bench = false,
  /** 難易度（1以上＝ハード以上：業物・神品・ハードの銘が出る） */
  difficulty = 0,
): EquipDrop {
  const r = CONFIG.rewards;
  const slot: EquipSlot = bench ? 'weapon' : weighted<EquipSlot>(h, [
    { item: 'weapon', weight: r.slotWeights.weapon },
    { item: 'armor', weight: r.slotWeights.armor },
    { item: 'netsuke', weight: r.slotWeights.netsuke },
  ]);
  const rarity = weighted<Rarity>(h, RARITIES.map((x) => ({ item: x, weight: rarityWeights[x] ?? 0 })));
  let defId: string;
  const pickLine = (c: CharId | null) => {
    const lines = linesFor(slot, c);
    return lineDefId(lines[randInt(h, lines.length)], tier);
  };
  let who: CharId | null = null;
  if (slot === 'weapon') {
    const others = CHAR_IDS.filter((c) => c !== sortieChar);
    who = !bench && random(h) < r.sortieCharWeapon ? sortieChar : others[randInt(h, others.length)];
    defId = pickLine(who);
  } else {
    const uniques = enemyIds.map((e) => `u_${e}`).filter((id) => EQUIPMENT[id]?.slot === slot);
    defId = uniques.length && random(h) < 0.5 ? uniques[randInt(h, uniques.length)] : pickLine(null);
  }
  if (rarity === 'ssr' && random(h) < CONFIG.equipment.meibutsuChance) {
    const cands = MEIBUTSU.filter((m) => m.slot === slot && m.tier <= tier && (slot !== 'weapon' || m.charId === who));
    if (cands.length) defId = cands[randInt(h, cands.length)].id;
  }
  const hard = difficulty >= 1;
  const c = CONFIG.equipment;
  const shin = hard && rarity === 'ssr' && random(h) < c.shinChance;
  const set = hard && rarity !== 'n' && random(h) < c.setChance ? EQUIP_SETS[randInt(h, EQUIP_SETS.length)].id : undefined;
  return { defId, rarity, affixes: rollAffixes(h, EQUIPMENT[defId].tier, rarity, { hard, shin }), ...(set ? { set } : {}), ...(shin ? { shin: true } : {}) };
}

// ---- 強化・限界突破 ----

export function enhanceCost(item: EquipItem): number {
  const c = CONFIG.equipment;
  const next = item.level + 1;
  return Math.round(c.enhanceBase * Math.pow(next, c.enhanceExp) * (c.enhanceTierBase + c.enhanceTierPer * item.tier));
}

export function enhance(p: Profile, res: Resources, itemId: number): OpResult<{ level: number; cost: number }> {
  const item = p.items.find((x) => x.id === itemId);
  if (!item) return { ok: false, error: 'no_item' };
  if (item.level >= levelCap(item)) return { ok: false, error: 'level_cap' };
  const cost = enhanceCost(item);
  if (res.zeni < cost) return { ok: false, error: 'zeni' };
  res.zeni -= cost;
  item.level++;
  return { ok: true, level: item.level, cost };
}

/** 鍛冶屋のLvで合成できる回数の上限（Lv2ごとに1回＝強化の上限+10。Lv18で+100） */
export const smithLbMax = (p: Profile) =>
  Math.min(CONFIG.equipment.lbMax, Math.floor((p.village?.levels?.kajiya ?? 0) / CONFIG.village.smithLevelsPerLb));
/** 鍛冶屋で届く強化の上限 */
export const smithLevelCap = (p: Profile) => levelCap({ lb: smithLbMax(p) });
/** 次の合成に要る鍛冶屋のLv */
export const smithLevelForLb = (lb: number) => (lb + 1) * CONFIG.village.smithLevelsPerLb;

export const limitBreakCost = (item: EquipItem) => CONFIG.equipment.limitBreakZeni * item.tier * (item.lb + 1);

/** 合成（限界突破）：同じ装備（同じ名前）でレア度が同じか上のものを1つ消費して、強化の上限を+10。素材の追加効果・強化は失われる。ロック中・装備中は素材にできない */
export function limitBreak(p: Profile, res: Resources, itemId: number, fodderId: number): OpResult<{ lb: number; cost: number }> {
  const item = p.items.find((x) => x.id === itemId);
  const fodder = p.items.find((x) => x.id === fodderId);
  if (!item || !fodder || item.id === fodder.id) return { ok: false, error: 'no_item' };
  // 素材：同じ装備（同じ名前＝defId）で、レアリティが同じか上
  if (fodder.defId !== item.defId || RARITIES.indexOf(fodder.rarity) < RARITIES.indexOf(item.rarity)) return { ok: false, error: 'not_same' };
  if (fodder.locked || isEquipped(p, fodder.id)) return { ok: false, error: 'fodder_in_use' };
  if (item.lb >= CONFIG.equipment.lbMax) return { ok: false, error: 'lb_max' };
  if (item.lb >= smithLbMax(p)) return { ok: false, error: 'smith' };
  const cost = limitBreakCost(item);
  if (res.zeni < cost) return { ok: false, error: 'zeni' };
  res.zeni -= cost;
  p.items = p.items.filter((x) => x.id !== fodder.id);
  item.lb++;
  return { ok: true, lb: item.lb, cost };
}

// ---- ロック・売却（報酬は銭のみ） ----

export function setLock(p: Profile, itemId: number, locked: boolean): OpResult<{ locked: boolean }> {
  const item = p.items.find((x) => x.id === itemId);
  if (!item) return { ok: false, error: 'no_item' };
  if (locked) item.locked = true;
  else delete item.locked;
  return { ok: true, locked: !!item.locked };
}

/** 売値（銭） */
export function sellValue(item: EquipItem): number {
  const c = CONFIG.equipment;
  return Math.round(c.dismantleBase * item.tier * (c.rarityMult[item.rarity] ?? 1) * (1 + 0.3 * item.level) * (1 + item.lb));
}

export function isEquipped(p: Profile, itemId: number): boolean {
  return CHAR_IDS.some((c) => {
    const s = p.sets[c];
    return s.weapon === itemId || s.armor === itemId || s.netsuke === itemId;
  });
}

/** 売れるか（ロック中・装備中は不可） */
export const canSell = (p: Profile, item: EquipItem) => !item.locked && !isEquipped(p, item.id);

/** 指定した品を売る。ロック中・装備中・無い品は飛ばす */
export function sell(p: Profile, res: Resources, itemIds: number[]): OpResult<{ zeni: number; count: number; itemIds: number[] }> {
  let zeni = 0;
  const sold: number[] = [];
  const want = new Set(itemIds);
  for (const item of p.items) {
    if (!want.has(item.id) || !canSell(p, item)) continue;
    zeni += sellValue(item);
    sold.push(item.id);
  }
  const gone = new Set(sold);
  p.items = p.items.filter((x) => !gone.has(x.id));
  res.zeni += zeni;
  return { ok: true, zeni, count: sold.length, itemIds: sold };
}
/** 旧名（分解＝売却） */
export const dismantle = sell;

/** 一括売却の条件。未ロック・未装備のうち、レアリティ・段・追加効果の数がそれぞれ以下、部位が slots に含まれるもの */
export interface SellFilter {
  maxRarity: Rarity;
  maxTier?: number;
  maxAffixes?: number;
  slots?: EquipSlot[];
}

export function sellTargets(p: Profile, f: SellFilter): EquipItem[] {
  const maxR = rarityRank(f.maxRarity);
  if (maxR < 0) return [];
  return p.items.filter(
    (x) =>
      canSell(p, x) &&
      rarityRank(x.rarity) <= maxR &&
      (f.maxTier === undefined || x.tier <= f.maxTier) &&
      (f.maxAffixes === undefined || x.affixes.length <= f.maxAffixes) &&
      (!f.slots || f.slots.includes(x.slot)),
  );
}

/** 一括売却の見積もり（profile は変えない） */
export function sellPreview(p: Profile, f: SellFilter): { itemIds: number[]; count: number; zeni: number } {
  const t = sellTargets(p, f);
  return { itemIds: t.map((x) => x.id), count: t.length, zeni: t.reduce((a, x) => a + sellValue(x), 0) };
}

export function sellByFilter(p: Profile, res: Resources, f: SellFilter): OpResult<{ zeni: number; count: number; itemIds: number[] }> {
  return sell(p, res, sellPreview(p, f).itemIds);
}

/** 自動売却の設定 */
export function setAutoSell(p: Profile, rarity: AutoSell, maxTier?: number | null): OpResult<{ autoSell: AutoSell; autoSellTier?: number }> {
  if (!['none', 'n', 'r'].includes(rarity)) return { ok: false, error: 'rarity' };
  if (maxTier !== undefined && maxTier !== null && !(Number.isInteger(maxTier) && maxTier >= 1 && maxTier <= 10)) return { ok: false, error: 'tier' };
  const s = { ...(p.settings ?? {}), autoSell: rarity };
  // maxTier を渡さなければ段の設定はそのまま。null で「段は問わない」
  if (maxTier === null) delete s.autoSellTier;
  else if (maxTier !== undefined) s.autoSellTier = maxTier;
  p.settings = s;
  return { ok: true, autoSell: rarity, ...(s.autoSellTier ? { autoSellTier: s.autoSellTier } : {}) };
}

/** 主人公の姿は全て解放済み。旧クライアントの図鑑アイコンと null も互換維持。 */
/** 月パスの姿は一度月パスを買えば選べる（買い切り）。now は互換のため */
export function setAvatar(p: Profile, enemyId: string | null, _now = 0): OpResult<{ avatar: string | null }> {
  if (isPremiumAvatar(enemyId) && !hasPremiumAvatars(p)) return { ok: false, error: 'monthly_required' };
  if (enemyId !== null && !isPlayerAvatar(enemyId) &&
      (typeof enemyId !== 'string' || !(isDexEnemy(enemyId) || DEX_ENEMIES.some(e => e.id === enemyId)) || !p.dex.includes(enemyId))) return { ok: false, error: 'avatar' };
  const s = { ...(p.settings ?? { autoSell: 'none' as AutoSell }) };
  if (enemyId) s.avatar = enemyId;
  else delete s.avatar;
  p.settings = s;
  return { ok: true, avatar: enemyId };
}

// ---- 装備セット（武器はキャラ専用、防具・根付は共通。同じ品は1人だけ） ----

export function equip(p: Profile, charId: CharId, itemId: number): OpResult {
  const item = p.items.find((x) => x.id === itemId);
  if (!item) return { ok: false, error: 'no_item' };
  if (item.slot === 'weapon' && item.charId !== charId) return { ok: false, error: 'wrong_char' };
  for (const c of CHAR_IDS) if (p.sets[c][item.slot] === itemId) p.sets[c][item.slot] = null;
  p.sets[charId][item.slot] = itemId;
  return { ok: true };
}

export function unequip(p: Profile, charId: CharId, slot: EquipSlot) {
  p.sets[charId][slot] = null;
}

export function equippedItems(p: Profile, charId: CharId): EquipItem[] {
  const s = p.sets[charId];
  return [s.weapon, s.armor, s.netsuke].map((id) => p.items.find((x) => x.id === id)).filter((x): x is EquipItem => !!x);
}

/** 合成（限界突破）の素材にできる装備（同じ装備でレア度が同じか上・ロック中と装備中は除く）。弱い順 */
export function limitBreakFodder(p: Profile, itemId: number): EquipItem[] {
  const item = p.items.find((x) => x.id === itemId);
  if (!item) return [];
  return p.items
    .filter((x) => x.id !== item.id && x.defId === item.defId && RARITIES.indexOf(x.rarity) >= RARITIES.indexOf(item.rarity) && !x.locked && !isEquipped(p, x.id))
    .sort((a, b) => RARITIES.indexOf(a.rarity) - RARITIES.indexOf(b.rarity) || a.tier - b.tier || a.level - b.level || a.id - b.id);
}
